import { call, put, select, takeEvery } from 'redux-saga/effects';

import { normalize } from 'normalizr';
import API_BASE_URL from '../../../../../redux/sagas/settings/apibaseurl';

import * as actions from '../../actions/routesExecutions';
import * as selectors from '../../reducers/routes';
import * as schemas from '../../schemas/routesExecutions';
import * as types from '../../types/routesExecutions';

import { store } from '@redux/storeConfig/store';
import camelcaseKeys from 'camelcase-keys';
import { endDateAdjustment } from 'utility/http/interceptors/interceptor-utils';
import * as authSelectors from '../../../../../redux/reducers/auth';
import * as alerts from '../../../../../redux/utils/alerts';
import { appIntl } from '../../../../../utility/context/IntlGlobalProvider';
import {
  changeToAlternateRouteService,
  saveRouteExecutionService
} from '../../services/routeExecution';

/* -------------------------------------------------------------------------- */
/*                            FETCH LIST EXECUTIONS                           */
/* -------------------------------------------------------------------------- */
function* fetchRoutesExecutions(action) {
  try {
    const filters = action.payload;
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);
      const response = yield call(fetch, `${API_BASE_URL}/route/getExecutions`, {
        method: 'POST',
        body: JSON.stringify(endDateAdjustment({ ...filters })),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const indicators = camelcaseKeys(jsonResult.data.indicators);
        const { scheduledBySchedule } = camelcaseKeys(jsonResult.data.scheduledBySchedule);
        const { totalNotShown } = camelcaseKeys(jsonResult.data.totalNotShown);
        const resultData = jsonResult.data.routesList.map((result) => camelcaseKeys(result));
        const {
          entities: { routesExecutions },
          result //order
        } = normalize(resultData, schemas.routesExecutions); //normalize data to byId and order
        yield put(
          actions.completeFetchingRoutesExecutions(routesExecutions, result, {
            ...indicators,
            scheduledBySchedule,
            totalNotShown
          })
        );
      } else {
        const error = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.routeFetchMonitorMessageError'
          }),
          errorNumber: jsonResult.number
        };
        // alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failFetchingRoutesExecutions(error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: appIntl().formatMessage({
        id: 'routes.routeFetchMonitorMessageError'
      }),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failFetchingRoutesExecutions(error));
  }
}

export function* watchFetchRoutesExecutions() {
  yield takeEvery(types.ROUTES_EXECUTIONS_FETCH_STARTED, fetchRoutesExecutions);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */
function* editRouteExecution(action) {
  const routeExecution = action.payload;
  const oldRouteExecution = yield select((state) =>
    selectors.getRoutesExecution(state, routeExecution.routeAssignmentId)
  );
  saveRouteExecutionService(
    {
      ...routeExecution,
      routeAssignmentId: oldRouteExecution.routeAssignmentId,
      unitName: oldRouteExecution.unitName
    },
    {
      successAction: (response) => {
        const editedRouteExecution = camelcaseKeys(response.data);
        store.dispatch(
          actions.completeEditRouteExecution({ ...routeExecution, ...editedRouteExecution })
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failEditRouteExecution(error));
      }
    }
  );
}

export function* watchEditRouteExecutionStarted() {
  yield takeEvery(types.ROUTE_EXECUTION_EDIT_STARTED, editRouteExecution);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */
function* changeToAlternateRoute(action) {
  const { routeAssignmentId, routeId, reason } = action.payload;
  changeToAlternateRouteService(
    { routeAssignmentId, routeId, reason },
    {
      successAction: (response) => {
        if (response.success)
          alerts.showSuccessMessage({
            success: appIntl().formatMessage({
              id: 'routes.successfullyChangeAlternateRoute'
            })
          });
        store.dispatch(actions.completeChangeToAlternateRoute(routeAssignmentId, routeId, reason));
      },
      errorAction: (error) => {
        const errorTxt = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.changeAlternateRouteMessageError'
          })
        };
        alerts.showErrorAlertFailChanges({ error: errorTxt });
        store.dispatch(actions.failChangeToAlternateRoute(error));
      }
    }
  );
}

export function* watchChangeToAlternateRouteStarted() {
  yield takeEvery(types.ROUTE_CHANGE_TO_ALTERNATE_ROUTE_STARTED, changeToAlternateRoute);
}

/* -------------------------------------------------------------------------- */
/*                        Assign Route Execution Unit                      */
/* -------------------------------------------------------------------------- */
function* assignRouteExecutionUnit(action) {
  const assignation = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/route/assignUnit`, {
        method: 'POST',
        body: JSON.stringify(assignation),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (
        response.status <= 300 &&
        jsonResult.success &&
        jsonResult.data.length === assignation.length
      ) {
        yield put(actions.completeAssignUnitRoutesExecutions(assignation));

        alerts.showSuccessMessage({
          success: appIntl().formatMessage({
            id: 'routes.unitWasSuccessfullyAssigned'
          })
        });
      } else {
        const error = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.unitAssignmentIsNotPossibleRightNow'
          }),
          errorNumber: jsonResult.number
        };
        alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failAssignUnitRoutesExecutions(error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: appIntl().formatMessage({
        id: 'routes.unitAssignmentIsNotPossibleRightNow'
      }),
      errorNumber: -1
    };
    alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failAssignUnitRoutesExecutions(error));
  }
}

export function* watchAsignRouteExecutionUnit() {
  yield takeEvery(types.ROUTES_EXECUTION_ASSIGN_UNIT_STARTED, assignRouteExecutionUnit);
}
